// src/services/apiService.js
import { toItalianTime } from "../utils/timeUtils";

// Base URL for the APIs - Updated to match existing files
const API_BASE_URL = "/sensori/api";

/**
 * Retrieves the list of available sensors
 * @returns {Promise<Array>} Promise tht returns the list of sensors
 */
// src/services/apiService.js - Funzione fetchSensors modificata
export const fetchSensors = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/getSensorsList.php`);

    if (!response.ok) {
      throw new Error(`Error in request: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }

    // Per evitare duplicati, usiamo un Set
    const uniqueSensorNames = new Set();
    let sensorList = [];

    // Handle different response formats from the API
    if (Array.isArray(data)) {
      sensorList = data;
    } else if (data.sensors && Array.isArray(data.sensors)) {
      sensorList = data.sensors;
    }

    // Filtra i duplicati
    const uniqueSensors = [];
    for (const sensor of sensorList) {
      const sensorName = typeof sensor === "string" ? sensor : sensor.name;
      if (!uniqueSensorNames.has(sensorName)) {
        uniqueSensorNames.add(sensorName);
        uniqueSensors.push(sensor);
      }
    }

    return uniqueSensors;
  } catch (error) {
    console.error("Error retrieving sensors:", error);
    throw error;
  }
};
/**
 * Checks if a sensor exists
 * @param {string} sensorName - Sensor name
 * @returns {Promise<boolean>} Promise that returns true if the sensor exists
 */
export const checkSensorExists = async (sensorName) => {
  try {
    // Use the same API endpoint for consistency
    const response = await fetch(`${API_BASE_URL}/getSensorsList.php`);

    if (!response.ok) {
      return false;
    }

    const data = await response.json();

    // Check if the sensor exists in the list
    if (Array.isArray(data)) {
      return data.some(
        (sensor) =>
          (typeof sensor === "string" && sensor === sensorName) ||
          (sensor.name && sensor.name === sensorName),
      );
    } else if (data.sensors && Array.isArray(data.sensors)) {
      return data.sensors.some(
        (sensor) =>
          (typeof sensor === "string" && sensor === sensorName) ||
          (sensor.name && sensor.name === sensorName),
      );
    }

    return false;
  } catch (error) {
    console.error("Error verifying sensor:", error);
    return false;
  }
};

/**
 * Retrieves data for a specific sensor
 * @param {string} sensorName - Sensor name
 * @param {Object} options - Query options (startDate, endDate, limit, aggregation)
 * @returns {Promise<Array>} Promise that returns the sensor data
 */
export const fetchSensorDataa = async (sensorName, options = {}) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append("sensor", sensorName);

    // Aggiungi parametri opzionali
    if (options.startDate) {
      queryParams.append("startDate", options.startDate);
    }

    if (options.endDate) {
      queryParams.append("endDate", options.endDate);
    }

    if (options.limit) {
      queryParams.append("limit", options.limit.toString());
    }

    if (options.aggregation) {
      queryParams.append("aggregation", options.aggregation);
    }

    // Aggiungi timeout per evitare richieste bloccanti
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 secondi di timeout

    // Esegui la richiesta usando getSensorsData.php
    const response = await fetch(
      `${API_BASE_URL}/getSensorsData.php?${queryParams.toString()}`,
      {
        signal: controller.signal,
      },
    );

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`Errore nella richiesta: ${response.status}`);
    }

    const data = await response.json();

    if (data.error) {
      throw new Error(data.error);
    }

    // Controlla errori specifici del sensore
    if (data[`error_${sensorName}`]) {
      throw new Error(data[`error_${sensorName}`]);
    }

    // CORRECTED: Protezione aggiuntiva per garantire che stiamo restituendo un array
    if (data[sensorName] && Array.isArray(data[sensorName])) {
      return data[sensorName];
    }

    // Se non ci sono dati per questo sensore, restituisci un array vuoto
    console.warn(`Nessun dato disponibile per il sensore ${sensorName}`);
    return [];
  } catch (error) {
    // Se l'errore è un timeout, aggiungi un messaggio più chiaro
    if (error.name === "AbortError") {
      console.error(
        `Timeout nella richiesta dati per il sensore ${sensorName}`,
      );
      throw new Error(
        `Timeout nella richiesta dati per il sensore ${sensorName}`,
      );
    }

    console.error(
      `Errore nel recupero dei dati per il sensore ${sensorName}:`,
      error,
    );
    // Assicurati di ritornare un array vuoto anche in caso di errore
    return [];
  }
};

export const fetchWeatherStationData = async (options = {}) => {
  try {
    const result = await fetchSensorDataa("Sensore5", options);

    // Normalizza i campi della risposta
    if (Array.isArray(result) && result.length > 0) {
      return result.map((item) => {
        return {
          id: item.id || 0,
          dateString: item.dateString || item.Date_Time,
          temperatura: item.temperatura || item.Temp1 || 0,
          umidita: item.umidita || item.Umid1 || 0,
          // Campi specifici meteo - Aggiunti tutti i possibili nomi di campo
          lightIntensity:
            item.lightIntensity ||
            item.light_intensity ||
            item["Light Intensity"] ||
            item.LightIntensity ||
            0,
          uvIndex:
            item.uvIndex ||
            item.uv_index ||
            item["UV Index"] ||
            item.UVIndex ||
            0,
          windSpeed:
            item.windSpeed ||
            item.wind_speed ||
            item["Wind Speed"] ||
            item.WindSpeed ||
            0,
          windDirection:
            item.windDirection ||
            item.wind_direction ||
            item["Wind Direction"] ||
            item.WindDirection ||
            0,
          rainGauge:
            item.rainGauge ||
            item.rain_gauge ||
            item["Rain Gauge"] ||
            item.RainGauge ||
            0,
          pressure:
            item.pressure ||
            item.barometric_pressure ||
            item["Barometric pressure"] ||
            item.Barometricpressure ||
            0,
          dewPoint:
            item.dewPoint ||
            item.PuntoRugiada ||
            item["PuntoRugiada"] ||
            item.dew_point ||
            0,
        };
      });
    }

    return result || [];
  } catch (error) {
    console.error("Errore nel recupero dati della stazione meteo:", error);
    return [];
  }
};
